<template>
    <div class="voice-agent-page-content">
        <div class="header-section">
            <div class="title-wrapper">
                <h1 class="main-title">{{ $t("voiceagent.batchCall") }}</h1>
                <p class="title-description">{{ $t("voiceagent.batchCallDescription") }}</p>
            </div>
        </div>

        <!-- Toplu Arama Oluşturma Formu -->
        <div class="section" v-if="showCreateForm">
            <div class="section-title">{{ $t("voiceagent.createNewBatchCall") }}</div>
            <div class="batch-call-form">
                <SupsisButton @click="showCreateForm = false" icon="arrow_back" style="margin-bottom: 20px;">
                    {{ $t("voiceagent.backToList") }}
                </SupsisButton>

                <div class="batch-call-cost-info">
                    <a-icon type="info-circle" />
                    {{ $t("voiceagent.batchCallCostInfo") }}
                </div>

                <a-form :form="form" layout="vertical" @submit="handleSubmit">
                    <a-form-item :label="$t('voiceagent.batchCallName')">
                        <a-input
                            v-decorator="[
                                'name',
                                {
                                    rules: batchNameRules
                                }
                            ]"
                            :placeholder="$t('voiceagent.enterCampaignName')"
                        />
                    </a-form-item>

                    <a-form-item :label="$t('voiceagent.callerNumber')">
                        <a-select
                            v-decorator="[
                                'fromNumber',
                                {
                                    rules: batchFromNumberRules
                                }
                            ]"
                            :placeholder="$t('voiceagent.selectNumber')"
                        >
                            <a-select-option v-for="number in phoneNumbers" :key="number.phone_number"
                                             :value="number.phone_number">
                                {{ number.nickname ? `${number.nickname} (${number.phone_number})` : number.phone_number
                                }}
                            </a-select-option>
                        </a-select>
                    </a-form-item>

                    <a-form-item :label="$t('voiceagent.voiceAgent')">
                        <a-select
                            v-decorator="[
                                'agentId',
                                {
                                    rules: batchAgentRules
                                }
                            ]"
                            :placeholder="$t('voiceagent.selectVoiceAgent')"
                            :loading="loadingAgents"
                            show-search
                            option-filter-prop="children"
                        >
                            <a-select-option v-for="agent in agents" :key="agent.agent_id" :value="agent.agent_id">
                                {{ agent.agent_name || agent.agent_id }}
                            </a-select-option>
                        </a-select>
                    </a-form-item>

                    <a-form-item :label="$t('voiceagent.recipients')">
                        <div class="upload-container">
                            <div class="upload-actions">
                                <a-upload
                                    :customRequest="handleFileUpload"
                                    :showUploadList="false"
                                    :beforeUpload="beforeUpload"
                                    accept=".csv"
                                >
                                    <SupsisButton>
                                        <i class="material-icons">upload</i> {{ $t("voiceagent.uploadCsvFile") }}
                                    </SupsisButton>
                                </a-upload>
                                <SupsisButton type="link" @click="downloadTemplate">
                                    <i class="material-icons">download</i> {{ $t("voiceagent.downloadTemplate") }}
                                </SupsisButton>
                            </div>

                            <a-alert
                                v-if="csvError"
                                type="error"
                                :message="csvError"
                                banner
                                closable
                                @close="csvError = ''"
                            />

                            <div v-if="csvData.length > 0" class="csv-info">
                                <div class="csv-file-info">
                                    <i class="material-icons">description</i>
                                    {{ fileName }}
                                    <span>{{ $t("voiceagent.personCount", { count: csvData.length }) }}</span>
                                    <SupsisButton
                                        type="link"
                                        size="small"
                                        @click="handleClearCsv"
                                    >
                                        <i class="material-icons">delete</i>
                                    </SupsisButton>
                                </div>
                            </div>
                        </div>
                    </a-form-item>

                    <a-form-item :label="$t('voiceagent.whenToCall')">
                        <a-radio-group
                            v-decorator="[
                                'callSchedule',
                                {
                                    initialValue: 'now'
                                }
                            ]"
                            @change="handleScheduleChange"
                        >
                            <a-radio value="now">{{ $t("voiceagent.callNow") }}</a-radio>
                            <a-radio value="schedule">{{ $t("voiceagent.schedule") }}</a-radio>
                        </a-radio-group>
                    </a-form-item>

                    <div v-if="isScheduled">
                        <a-form-item :label="$t('voiceagent.callTime')">
                            <a-date-picker
                                v-decorator="[
                                    'scheduleDate',
                                    {
                                        rules: batchScheduleDateRules
                                    }
                                ]"
                                :showTime="{ format: 'HH:mm' }"
                                format="YYYY-MM-DD HH:mm"
                                :placeholder="$t('voiceagent.selectDateTime')"
                                style="width: 100%"
                            />
                        </a-form-item>

                        <a-form-item :label="$t('voiceagent.timezone')">
                            <a-select
                                v-decorator="[
                                    'timezone',
                                    {
                                        initialValue: 'Europe/Istanbul',
                                        rules: batchTimezoneRules
                                    }
                                ]"
                                :placeholder="$t('voiceagent.selectTimezone')"
                            >
                                <a-select-option value="Europe/Istanbul">{{ $t("voiceagent.timezoneEuropeIstanbul") }}</a-select-option>
                                <a-select-option value="Europe/London">{{ $t("voiceagent.timezoneEuropeLondon") }}</a-select-option>
                                <a-select-option value="America/New_York">{{ $t("voiceagent.timezoneAmericaNewYork") }}</a-select-option>
                                <a-select-option value="Asia/Tokyo">{{ $t("voiceagent.timezoneAsiaTokyo") }}</a-select-option>
                            </a-select>
                        </a-form-item>
                    </div>

                    <a-form-item>
                        <div class="form-actions">
                            <SupsisButton type="primary" @click="handleSubmit" :loading="submitting"
                                          :disabled="!canSubmit">
                                {{ $t("voiceagent.startBatchCall") }}
                            </SupsisButton>
                            <SupsisButton @click="showCreateForm = false">
                                {{ $t("voiceagent.cancel") }}
                            </SupsisButton>
                        </div>
                    </a-form-item>
                </a-form>
            </div>
        </div>

        <!-- Toplu Arama Listesi -->
        <div class="section" v-else>
            <div class="table-wrapper">
                <div class="toolbar">
                    <div class="left">
                        <a-input-search
                            style="width: 300px; margin: unset !important"
                            v-model="searchText"
                            :placeholder="$t('voiceagent.search')"
                            class="search-input"
                            @input="onSearch"
                        />
                    </div>
                    <div class="right">
                        <PermissionController :permissions='["voice-agent.create"]'>
                            <SupsisButton primary @click="showCreateForm = true">
                                <i class="material-icons">add</i>
                                {{ $t("voiceagent.newBatchCall") }}
                            </SupsisButton>
                        </PermissionController>
                    </div>
                </div>

                <a-spin :spinning="loading">
                    <SupsisTable
                        :columns="tableColumns"
                        :rows="paginatedBatchCalls"
                        :top-pagination="true"
                        empty-data-bordered
                        :bottom-pagination="false"
                        :pagination-cursor="pagination"
                        :options="{
                            rowStyleOption,
                            clipboardOption,
                            fixedHeader: true,
                            id: 'batch-calls-table',
                            borderX: true,
                            borderY: true,
                        }"
                        @cursor-changed="(cursor) => (pagination = cursor)"
                    />
                </a-spin>
            </div>
        </div>

        <!-- Alıcılar Tablosu (Sağ Panel) -->
        <a-drawer
            :title="$t('voiceagent.recipients')"
            placement="right"
            :visible="showRecipients"
            :width="600"
            @close="showRecipients = false"
        >
            <a-table
                :columns="recipientColumns"
                :dataSource="csvData"
                :pagination="{ pageSize: 10 }"
            >
                <template slot="index" slot-scope="text, record, index">
                    {{ index + 1 }}
                </template>
            </a-table>
        </a-drawer>
    </div>
</template>

<script>
import { VOICE_AGENT_API } from "../../../../../api/voiceAgent";
import moment from "moment";
import Papa from "papaparse";
import SupsisTable from "../../../../../components/SupsisTable.vue";
import SupsisButton from "../../../Settings/components/SupsisButton.vue";
import _ from "lodash";

export default {
    name: "VoiceAgentBatchCall",
    components: {
        SupsisTable,
        SupsisButton,
    },
    props: {
        channelId: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            form: this.$form.createForm(this),
            loading: false,
            submitting: false,
            showCreateForm: false,
            isScheduled: false,
            showRecipients: false,
            phoneNumbers: [],
            agents: [],
            loadingAgents: false,
            batchCalls: [],
            csvData: [],
            fileName: "",
            csvError: "",
            searchText: "",
            pagination: {
                page: 1,
                perPage: 10,
                total: 0,
            },
            rowStyleOption: {
                stripe: true,
            },
            clipboardOption: {
                copy: true,
                paste: false,
                cut: false,
                delete: false,
            },
            recipientColumns: [
                {
                    title: "ID",
                    key: "index",
                    scopedSlots: { customRender: "index" },
                    width: 60,
                },
            ],
        };
    },
    computed: {
        batchNameRules() {
            return [{ required: true, message: this.$t("voiceagent.pleaseEnterCampaignName") }];
        },
        batchFromNumberRules() {
            return [{ required: true, message: this.$t("voiceagent.pleaseSelectNumber") }];
        },
        batchAgentRules() {
            return [{ required: true, message: this.$t("voiceagent.pleaseSelectVoiceAgent") }];
        },
        batchScheduleDateRules() {
            return [{ required: this.isScheduled, message: this.$t("voiceagent.pleaseSelectDate") }];
        },
        batchTimezoneRules() {
            return [{ required: this.isScheduled, message: this.$t("voiceagent.pleaseSelectTimezone") }];
        },
        tableColumns() {
            return [
                {
                    key: "name",
                    field: "name",
                    title: this.$t("voiceagent.campaignName"),
                    renderBodyCell: ({ row }) => {
                        return <span>{row.name || this.$t("voiceagent.unnamedCampaign")}</span>;
                    },
                },
                {
                    key: "from_number",
                    field: "from_number",
                    title: this.$t("voiceagent.callerNumberLabel"),
                    renderBodyCell: ({ row }) => {
                        return <span>{row.from_number}</span>;
                    },
                },
                {
                    key: "status",
                    field: "status",
                    title: this.$t("voiceagent.statusLabel"),
                    renderBodyCell: ({ row }) => {
                        return (
                            <a-tag color={this.getStatusColor(row.status)}>{this.getStatusText(row.status)}</a-tag>
                        );
                    },
                },
                {
                    key: "progress",
                    field: "progress",
                    title: this.$t("voiceagent.progress"),
                    renderBodyCell: ({ row }) => {
                        return (
                            <div>
                                <div class="progress-text">
                                    {this.$t("voiceagent.progressSent", {
                                        current: row.sent || 0,
                                        total: row.total || 0,
                                    })}
                                </div>
                                <div class="progress-text">
                                    {this.$t("voiceagent.progressAnswered", {
                                        current: row.picked_up || 0,
                                        total: row.total || 0,
                                    })}
                                </div>
                                <div class="progress-text">
                                    {this.$t("voiceagent.progressCompleted", {
                                        current: row.completed || 0,
                                        total: row.total || 0,
                                    })}
                                </div>
                            </div>
                        );
                    },
                },
                {
                    key: "scheduled_timestamp",
                    field: "scheduled_timestamp",
                    title: this.$t("voiceagent.scheduledTime"),
                    renderBodyCell: ({ row }) => {
                        return <span>{this.formatDate(row.scheduled_timestamp)}</span>;
                    },
                },
                {
                    key: "last_sent_timestamp",
                    field: "last_sent_timestamp",
                    title: this.$t("voiceagent.lastCall"),
                    renderBodyCell: ({ row }) => {
                        return <span>{this.formatDate(row.last_sent_timestamp)}</span>;
                    },
                },
                {
                    key: "actions",
                    field: "actions",
                    title: this.$t("voiceagent.actions"),
                    renderBodyCell: ({ row }) => {
                        return (
                            <div class="actions">
                                {row.tasks_url && (
                                    <SupsisButton
                                        class="details-btn"
                                        onClick={() => this.viewDetails(row)}
                                        ghost
                                        icon="visibility"
                                        small
                                    />
                                )}
                                <SupsisButton
                                    class="logs-btn"
                                    onClick={() => this.viewCallLogs(row)}
                                    ghost
                                    icon="history"
                                    small
                                />
                            </div>
                        );
                    },
                },
            ];
        },
        canSubmit() {
            return this.csvData.length > 0;
        },
        filteredBatchCalls() {
            if (!this.searchText) {
                return this.batchCalls;
            }
            const searchLower = this.searchText.toLowerCase();
            return this.batchCalls.filter((call) => {
                const name = _.get(call, "name", "").toLowerCase();
                const fromNumber = _.get(call, "from_number", "").toLowerCase();
                return name.includes(searchLower) || fromNumber.includes(searchLower);
            });
        },
        paginatedBatchCalls() {
            const start = (this.pagination.page - 1) * this.pagination.perPage;
            const end = start + this.pagination.perPage;
            return this.filteredBatchCalls.slice(start, end);
        },
    },
    mounted() {
        this.recipientColumns[0].title = this.$t("voiceagent.receiverId");
        this.fetchBatchCalls();
        this.fetchPhoneNumbers();
        this.fetchAgents();
    },
    methods: {
        async fetchBatchCalls() {
            this.loading = true;
            try {
                const batchCalls = await VOICE_AGENT_API.calls.listBatchCalls();
                this.batchCalls = batchCalls.sort((a, b) => {
                    const timestampA = a.scheduled_timestamp || 0;
                    const timestampB = b.scheduled_timestamp || 0;
                    return timestampB - timestampA;
                });
                this.pagination.total = this.batchCalls.length;
            } catch (error) {
                console.error("Toplu arama kampanyaları alınamadı:", error);
                this.$message.error(this.$t("voiceagent.errorLoadingBatchCalls"));
            } finally {
                this.loading = false;
            }
        },

        async fetchPhoneNumbers() {
            try {
                const numbers = await VOICE_AGENT_API.numbers.list();
                this.phoneNumbers = numbers;
            } catch (error) {
                console.error("Telefon numaraları alınamadı:", error);
                this.$message.error(this.$t("voiceagent.errorLoadingPhoneNumbers"));
            }
        },

        async fetchAgents() {
            this.loadingAgents = true;
            try {
                const agents = await VOICE_AGENT_API.agents.list(true);
                this.agents = agents || [];
            } catch (error) {
                console.error("Ses ajanları alınamadı:", error);
                this.$message.error(this.$t("voiceagent.errorLoadingAgents"));
            } finally {
                this.loadingAgents = false;
            }
        },

        onSearch() {
            this.pagination.page = 1;
        },

        formatDate(timestamp) {
            if (!timestamp) return "-";
            return moment(timestamp).format("DD.MM.YYYY HH:mm");
        },

        getStatusColor(status) {
            const statusColors = {
                pending: "orange",
                scheduled: "blue",
                sending: "purple",
                sent: "green",
                failed: "red",
                cancelled: "gray",
            };
            return statusColors[status] || "blue";
        },

        getStatusText(status) {
            const statusTexts = {
                pending: this.$t("voiceagent.statusPending"),
                scheduled: this.$t("voiceagent.statusScheduled"),
                sending: this.$t("voiceagent.statusSending"),
                sent: this.$t("voiceagent.statusSent"),
                failed: this.$t("voiceagent.statusFailed"),
                cancelled: this.$t("voiceagent.statusCancelled"),
            };
            return statusTexts[status] || status;
        },

        viewDetails(record) {
            if (record.tasks_url) {
                window.open(record.tasks_url, "_blank");
            }
        },

        viewCallLogs(record) {
            if (record.batch_call_id) {
                this.$router.push({
                    name: "voice-agent-dashboard-call-history",
                    params: {
                        id: this.channelId,
                    },
                    query: {
                        filter_batch_call_id: record.batch_call_id,
                        tab: "call-history",
                    },
                });
            }
        },

        handleScheduleChange(e) {
            this.isScheduled = e.target.value === "schedule";
        },

        downloadTemplate() {
            const csvContent = `phone number,name,city,company,product,account_id
+905553332211,Ahmet Yılmaz,İstanbul,ABC Şirketi,Premium Paket,12345
+905553332212,Ayşe Kaya,Ankara,XYZ Ltd,Standart Plan,23456
+905553332213,Mehmet Demir,İzmir,123 Holding,Gold Paket,34567
+905553332214,Ali Veli,Bursa,Veli A.Ş.,Silver Plan,45678
+905553332215,Selami Yılmaz,Adana,YZA Ticaret,Basic Plan,56789`;

            const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
            const link = document.createElement("a");
            const url = URL.createObjectURL(blob);

            link.setAttribute("href", url);
            link.setAttribute("download", "batch_call_template.csv");
            link.style.visibility = "hidden";

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },

        beforeUpload(file) {
            const isCsv = file.type === "text/csv" || file.name.endsWith(".csv");
            if (!isCsv) {
                this.$message.error(this.$t("voiceagent.csvOnlyAllowed"));
            }
            return isCsv;
        },

        handleFileUpload({ file }) {
            this.fileName = file.name;
            this.csvError = "";

            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    Papa.parse(e.target.result, {
                        header: true,
                        skipEmptyLines: true,
                        complete: (results) => {
                            if (results.errors.length > 0) {
                                this.csvError = this.$t("voiceagent.csvInvalid", { msg: results.errors[0].message });
                                this.csvData = [];
                                return;
                            }

                            // Doğru sütun adlarını kontrol et
                            const requiredColumns = ["phone number"];
                            const missingColumns = requiredColumns.filter(col => !results.meta.fields.includes(col));

                            if (missingColumns.length > 0) {
                                this.csvError = this.$t("voiceagent.csvMissingColumns", {
                                    columns: missingColumns.join(", "),
                                });
                                this.csvData = [];
                                return;
                            }

                            // Dinamik kolonları algıla
                            const columns = results.meta.fields;

                            // Tablo sütunlarını güncelle
                            this.recipientColumns = [
                                {
                                    title: this.$t("voiceagent.receiverId"),
                                    key: "index",
                                    scopedSlots: { customRender: "index" },
                                    width: 60,
                                },
                            ];

                            // Her kolonu tablo sütunlarına ekle
                            columns.forEach(column => {
                                this.recipientColumns.push({
                                    title: this.formatColumnName(column),
                                    dataIndex: column,
                                    key: column,
                                });
                            });

                            // Telefon numaralarını doğrula
                            const processedData = results.data.map(row => {
                                // Tüm kolonları kopyala
                                const data = { ...row };

                                // Telefon numarası anahtarını normalize et
                                data.phone = row["phone number"];

                                return data;
                            });

                            // Geçersiz telefon numaralarını kontrol et
                            const invalidPhones = processedData.filter(row => !row.phone || !row.phone.startsWith("+"));
                            if (invalidPhones.length > 0) {
                                this.csvError = this.$t("voiceagent.csvInvalidPhoneNumbers", {
                                    count: invalidPhones.length,
                                });
                            }

                            this.csvData = processedData;
                            this.showRecipients = true;
                        },
                    });
                } catch (error) {
                    this.csvError = this.$t("voiceagent.csvProcessingFailed", { msg: error.message });
                    this.csvData = [];
                }
            };
            reader.readAsText(file);
        },

        handleClearCsv() {
            this.csvData = [];
            this.fileName = "";
            this.csvError = "";
        },

        handleSubmit(e) {
            e.preventDefault();

            this.form.validateFields(async (err, values) => {
                if (err) return;

                if (this.csvData.length === 0) {
                    this.$message.error(this.$t("voiceagent.pleaseUploadRecipientList"));
                    return;
                }

                this.submitting = true;

                try {
                    // Arama görevlerini hazırla
                    const tasks = this.csvData.map(recipient => {
                        const task = {
                            to_number: recipient.phone,
                        };

                        // Seçilen ajanı bu çağrı için override et
                        if (values.agentId) {
                            task.override_agent_id = values.agentId;
                        }

                        // Dinamik değişkenleri ekle
                        const dynamicVars = {};
                        let hasDynamicVars = false;

                        // Tüm alanları kontrol et (phone number ve boş olanlar hariç)
                        Object.keys(recipient).forEach(key => {
                            if (key !== "phone" && key !== "phone number" && recipient[key]) {
                                // Alan adını camelCase formatına çevir (customer_name formatında)
                                const varName = key.replace(/\s+/g, "_").toLowerCase();
                                dynamicVars[varName] = recipient[key];
                                hasDynamicVars = true;
                            }
                        });

                        // Dinamik değişkenler varsa ekle
                        if (hasDynamicVars) {
                            task.retell_llm_dynamic_variables = dynamicVars;
                        }

                        return task;
                    });

                    // Batch call verilerini hazırla
                    const batchCallData = {
                        fromNumber: values.fromNumber,
                        name: values.name,
                        tasks: tasks,
                    };

                    // Planlı arama ise timestamp ekle
                    if (this.isScheduled && values.scheduleDate) {
                        const date = values.scheduleDate.toDate();
                        // Unix timestamp (milisaniye cinsinden)
                        batchCallData.triggerTimestamp = date.getTime();
                    }

                    // API'yi çağır
                    const result = await VOICE_AGENT_API.calls.createBatchCall(batchCallData);

                    this.$message.success(this.$t("voiceagent.batchCallCreated"));
                    this.showCreateForm = false;
                    this.fetchBatchCalls();

                    // Formu sıfırla
                    this.form.resetFields();
                    this.csvData = [];
                    this.fileName = "";

                } catch (error) {
                    console.error("Toplu arama oluşturulurken hata:", error);
                    this.$message.error(
                        this.$t("voiceagent.batchCallCreateFailed", {
                            msg: error.error || error.message,
                        })
                    );
                } finally {
                    this.submitting = false;
                }
            });
        },
        formatColumnName(column) {
            // Kolon adını daha okunabilir hale getir
            return column
                .split("_")
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(" ");
        },
    },
    watch: {
        pagination: {
            handler() {
                // Sayfa değişikliğinde yapılacak işlemler
            },
            deep: true,
        },
    },
};
</script>

<style scoped lang="scss">
.voice-agent-page-content {
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 0 20px;

    .header-section {
        margin-bottom: 5px;
    }

    .title-wrapper {
        display: flex;
        align-items: center;
        gap: 15px;
        margin-bottom: 20px;
    }

    .main-title {
        font-size: 24px;
        font-weight: 500;
        margin: 0;
    }

    .title-description {
        font-size: 14px;
        font-weight: 400;
        color: #8c8c8c;
        margin: 0;
    }

    .section {
        display: flex;
        flex-direction: column;
        gap: 15px;
    }

    .table-wrapper {
        display: flex;
        flex-direction: column;
        gap: 10px;

        .toolbar {
            width: 100%;
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 10px 0;
            gap: 5px;

            .left {
                display: flex;
                gap: 5px;
                align-items: center;
            }

            .right {
                display: flex;
                gap: 5px;
            }
        }
    }

    #batch-calls-table {
        .actions {
            display: flex;
            gap: 5px;
        }
    }

    .batch-call-form {
        max-width: 800px;
    }

    .batch-call-cost-info {
        margin-bottom: 20px;
        padding: 8px 12px;
        background-color: #f9f9f9;
        border-radius: 4px;
        color: #888;
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 13px;
    }

    .upload-container {
        border: 1px dashed #d9d9d9;
        border-radius: 4px;
        padding: 16px;
        background-color: #fafafa;
    }

    .upload-actions {
        display: flex;
        align-items: center;
        margin-bottom: 16px;
        gap: 10px;
    }

    .csv-info {
        margin-top: 16px;
    }

    .csv-file-info {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 14px;

        .material-icons {
            font-size: 18px;
            color: #52c41a;
        }

        span {
            color: #888;
            margin-left: 4px;
        }
    }

    .form-actions {
        display: flex;
        gap: 12px;
    }

    .progress-text {
        margin-bottom: 4px;
        font-size: 12px;
    }
}
</style>
